import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  listOffboardings, startOffboarding, setOffboardingTask, completeOffboarding, cancelOffboarding,
  getOffboardingImpact, type OffboardingDto, type ImpactHoleDto, type ResignKind,
} from '../../api/hr-offboarding';
import { listEmployees, type EmployeeRowDto } from '../../api/hr-employee';
// 기준 설정 (2026-08-28 · 판 7) — 종류의 «이름»과 «씀 여부»는 회사가 정한다.
//   못 읽으면 아래 기본 목록이 그대로 산다(깜빡이지 않는다).
import { useCompanyCodes } from '../../lib/company-codes';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 퇴사 처리 — 담당자 화면 (시안 A6, migration 131).
 *
 * ★★ **퇴사는 상태 한 칸이 아니라 목록이다.** 그래서 체크리스트가 화면의 절반이고, 필수 항목이
 *    남으면 확정이 막힌다(무엇이 남았는지는 서버가 이름으로 말해 준다).
 * ★★ **«이 사람이 사라지면»** — 부서장·대표·담당자·결재에 박혀 있는 자리를 먼저 보여 준다.
 *    막지는 않는다. 막으면 퇴사 처리를 못 하는데 사람은 이미 나갔다.
 * ★ 확정해도 **재직 상태는 안 바뀐다** — 그것은 퇴직 발령의 일이라, 확정 뒤에 그 길을 안내한다.
 */
const RESIGN_KINDS: Array<{ key: ResignKind; label: string }> = [
  { key: 'VOLUNTARY', label: '자진 퇴사' },
  { key: 'CONTRACT_END', label: '계약 만료' },
  { key: 'DISMISSAL', label: '권고 · 해고' },
  { key: 'OTHER', label: '기타' },
];

const STATUS_LABEL: Record<string, string> = { OPEN: '진행', DONE: '완료', CANCELED: '되돌림' };
const STATUS_TONE: Record<string, string> = { OPEN: 'is-wait', DONE: 'is-ok', CANCELED: '' };

export function OffboardingPage() {
  // ★ 회사가 고친 이름을 얹는다 — 「권고 · 해고」를 다르게 부르는 회사가 있다.
  const codes = useCompanyCodes('hr');
  const resignKinds = codes.apply('resign_kind', RESIGN_KINDS);
  const [rows, setRows] = useState<OffboardingDto[]>([]);
  const [picked, setPicked] = useState<OffboardingDto | null>(null);
  const [holes, setHoles] = useState<ImpactHoleDto[]>([]);
  const [employees, setEmployees] = useState<EmployeeRowDto[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [form, setForm] = useState({
    userId: '', resignDate: '', resignKind: 'VOLUNTARY' as ResignKind, reason: '', handoverUserId: '',
  });

  const load = useCallback(async () => {
    const res = await listOffboardings();
    setRows(res.offboardings);
    // 고른 것이 목록에서 바뀌었으면 새 값으로 갈아 끼운다.
    setPicked((cur) => (cur ? res.offboardings.find((r) => r.id === cur.id) ?? null : null));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
        return;
      }
      try {
        const emp = await listEmployees();
        if (alive) setEmployees(emp.rows.filter((e) => e.employmentStatus !== 'RESIGNED'));
      } catch { /* 사람 고르기만 못 쓴다 */ }
    })();
    return () => { alive = false; };
  }, [load]);

  // 고른 사람이 바뀌면 «이 사람이 사라지면» 을 다시 센다.
  const pick = useCallback(async (row: OffboardingDto) => {
    setPicked(row);
    setDone(null);
    setHoles([]);
    try {
      setHoles(await getOffboardingImpact(row.userId));
    } catch { /* 그 절만 비운다 */ }
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setError(null);
    setDone(null);
    if (!form.userId || !form.resignDate) {
      setError('대상과 퇴사일을 골라 주세요.');
      return;
    }
    setBusy(true);
    try {
      const created = await startOffboarding({
        userId: form.userId,
        resignDate: form.resignDate,
        resignKind: form.resignKind,
        reason: form.reason.trim() || null,
        handoverUserId: form.handoverUserId || null,
      });
      await load();
      await pick(created);
      setForm({ ...form, userId: '', reason: '', handoverUserId: '' });
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '시작하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const complete = async (row: OffboardingDto) => {
    setBusy(true);
    setError(null);
    try {
      const res = await completeOffboarding(row.id);
      setDone(res.next.message);
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '확정하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/offboarding')}
        scope={scopeFor('/erp/hr/offboarding')}
        title="퇴사 처리"
        description="면담부터 정산까지의 목록을 채우고, 마지막에 퇴직 발령을 시행합니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">퇴사 처리를 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          {done && (
            <div className="hrpage__note" role="status">
              {done} <Link to="/erp/hr/assignments">인사발령으로 가기</Link>
            </div>
          )}

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              퇴사 처리 시작
              <span className="hrpage__panels">시작해도 재직 상태는 그대로입니다 — 상태는 퇴직 발령이 바꿉니다</span>
            </h2>
            <div className="hrcert__form">
              <label className="hrcert__field">
                <span>대상</span>
                <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}>
                  <option value="">사람 고르기</option>
                  {employees.map((e) => (
                    <option key={e.userId} value={e.userId}>{e.name} · {e.teamName ?? '회사 직속'}</option>
                  ))}
                </select>
              </label>
              <label className="hrcert__field">
                <span>퇴사일</span>
                <input type="date" value={form.resignDate} onChange={(e) => setForm({ ...form, resignDate: e.target.value })} />
              </label>
              <label className="hrcert__field">
                <span>구분</span>
                <select
                  value={form.resignKind}
                  onChange={(e) => setForm({ ...form, resignKind: e.target.value as ResignKind })}
                >
                  {resignKinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
              </label>
              <label className="hrcert__field">
                <span>인수인계자</span>
                <select value={form.handoverUserId} onChange={(e) => setForm({ ...form, handoverUserId: e.target.value })}>
                  <option value="">고르지 않음</option>
                  {employees.map((e) => (
                    <option key={e.userId} value={e.userId}>{e.name} · {e.teamName ?? '회사 직속'}</option>
                  ))}
                </select>
              </label>
              <label className="hrcert__field">
                <span>사유 · 메모</span>
                <input
                  value={form.reason} maxLength={300} placeholder="예) 이직 — 08-06 면담 완료"
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </label>
              <Button variant="primary" onClick={start} disabled={busy}>
                {busy ? '여는 중…' : '시작하기'}
              </Button>
            </div>
          </section>

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              퇴사 처리 목록
              <span className="hrpage__panels">{rows.length}건</span>
            </h2>
            {rows.length === 0 ? (
              <p className="hrpage__empty">진행 중인 퇴사 처리가 없습니다.</p>
            ) : (
              <table className="hrpage__tbl">
                <thead>
                  <tr>
                    <th>퇴사일</th><th>대상</th><th>구분</th><th>인수인계</th>
                    <th className="hrpage__num">진행</th><th className="hrpage__act">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={picked?.id === r.id ? 'is-picked' : undefined}>
                      <td>{r.resignDate}</td>
                      <td><b>{r.userName ?? '—'}</b></td>
                      <td>{r.resignKindLabel}</td>
                      <td>{r.handoverUserName ?? <span className="hrpage__dim">없음</span>}</td>
                      <td className="hrpage__num">{r.progress.done} / {r.progress.total}</td>
                      <td className="hrpage__act">
                        <span className={`hrpage__pill ${STATUS_TONE[r.status] ?? ''}`}>
                          {STATUS_LABEL[r.status] ?? r.status}
                        </span>
                        <Button variant="secondary" size="sm" onClick={() => pick(r)}>열기</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {picked && (
            <div className="hrpage__row">
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  {picked.userName ?? '—'} · 체크리스트
                  <span className="hrpage__panels">{picked.progress.done} / {picked.progress.total} 완료</span>
                </h2>
                <table className="hrpage__tbl">
                  <tbody>
                    {picked.checklist.map((c) => (
                      <tr key={c.key}>
                        <td>
                          <label className="hroff__check">
                            <input
                              type="checkbox" checked={c.done} disabled={busy || picked.status !== 'OPEN'}
                              onChange={(e) => run(() => setOffboardingTask(picked.id, c.key, e.target.checked))}
                            />
                            <span>
                              <b>{c.label}</b>
                              {c.required ? <span className="hroff__req">필수</span> : null}
                              <span className="hrpage__dim"> · {c.hint}</span>
                            </span>
                          </label>
                        </td>
                        <td className="hrpage__act">
                          {c.doneAt ? <span className="hrpage__dim">{c.doneAt.slice(0, 10)}</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {picked.status === 'OPEN' && (
                  <div className="hroff__actions">
                    {picked.progress.blocking.length > 0 && (
                      <span className="hrpage__dim">남은 필수 — {picked.progress.blocking.join(' · ')}</span>
                    )}
                    <Button variant="primary" disabled={busy || !picked.canComplete} onClick={() => complete(picked)}
                    >퇴사 확정</Button>
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => run(() => cancelOffboarding(picked.id))}
                    >되돌리기</Button>
                  </div>
                )}
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  이 사람이 사라지면
                  <span className="hrpage__panels">확정을 막지는 않습니다 — 먼저 채워 두라는 말입니다</span>
                </h2>
                {holes.length === 0 ? (
                  <p className="hrpage__empty">비는 자리가 없습니다.</p>
                ) : (
                  <table className="hrpage__tbl">
                    <tbody>
                      {holes.map((h) => (
                        <tr key={h.kind}>
                          <td>
                            <b>{h.label}</b>
                            {h.detail.length > 0 ? <span className="hrpage__dim"> · {h.detail.join(', ')}</span> : null}
                            <div className="hroff__why">
                              <AlertCircle size={13} aria-hidden /> {h.consequence}
                            </div>
                          </td>
                          <td className="hrpage__num">{h.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}
